import { useCallback, useEffect, useRef } from "react";
import useToast from "../../hooks/useToast";
import useAuth from "../../hooks/useAuth"; 
import useNetworkStatus from "../../hooks/useNetworkStatus";
import { ReactComponent as OfflineIcon } from "../../assets/icons/Offline.svg";
import { ReactComponent as OnlineIcon } from "../../assets/icons/Online.svg";

const DEFAULT_OFFLINE_DURATION = 4000;
const DEFAULT_ONLINE_DURATION = 4000;

const OFFLINE_DEBOUNCE_MS = 1000;
const ONLINE_DEBOUNCE_MS = 1000;
const RESUME_GRACE_MS = 2000;

const NetworkStatusToastTrigger = () => {
  const { isOnline } = useNetworkStatus();
  const toast = useToast();
  const wasOnline = useRef(isOnline);
  const { isLogged } = useAuth();
  const offlineTimeout = useRef();
  const onlineTimeout = useRef();
  const resumeTimeout = useRef();
  const isResuming = useRef(false);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        isResuming.current = true;
        clearTimeout(resumeTimeout.current);
        resumeTimeout.current = setTimeout(() => {
          isResuming.current = false;
        }, RESUME_GRACE_MS);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(resumeTimeout.current);
    };
  }, []);
 
  const showToast = useCallback(({ primaryMessage, secondaryMessage, icon, duration }) => {
    toast.triggerToast({
      type: "info",
      primaryMessage,
      secondaryMessage,
      icon,
      autoHideDuration: duration,
    });
  }, [toast]);
  
  const debounceToast = (timeoutRef, delay, fn) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(fn, delay);
  };

  useEffect(() => {
    if (!isLogged) return;

    if (wasOnline.current !== isOnline) {
      if (!isOnline) {
        debounceToast(offlineTimeout, OFFLINE_DEBOUNCE_MS, () => {
          if (!isOnline && !isResuming.current) {
            showToast({
              primaryMessage: "You are offline",
              secondaryMessage: "Check your connection for full access to features.",
              icon: OfflineIcon,
              duration: DEFAULT_OFFLINE_DURATION,
            });
          }
        });
      } else if (wasOnline.current === false && isOnline === true) {
        debounceToast(onlineTimeout, ONLINE_DEBOUNCE_MS, () => {
          if (isOnline) {
            showToast({
              primaryMessage: "You are back online",
              secondaryMessage: "Full access to features restored.",
              icon: OnlineIcon,
              duration: DEFAULT_ONLINE_DURATION,
            });
          }
        });
      }
      wasOnline.current = isOnline;
    }
    // Save refs for cleanup
    const offlineTimeoutId = offlineTimeout.current;
    const onlineTimeoutId = onlineTimeout.current;
    return () => {
      clearTimeout(offlineTimeoutId);
      clearTimeout(onlineTimeoutId);
    };
  }, [isOnline, isLogged, showToast]);

  return null;
};

export default NetworkStatusToastTrigger;